import { Skeleton, Typography } from '@mui/material';
import { useScanCategory } from '@scans/hooks/useScanCategory';
import { useScanInfo } from '@scans/hooks/useScanInfo';
import { useEffect, useRef, useState } from 'react';

interface ScanCategoryDetailsProps {
  scanId: string;
  projectId: string;
}

export const ScanCategoryDetails = ({ scanId, projectId }: ScanCategoryDetailsProps) => {
  const { getScanInfo } = useScanInfo();
  const { getScanCategory } = useScanCategory();

  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getScanInfoRef = useRef(getScanInfo);
  const getScanCategoryRef = useRef(getScanCategory);
  getScanInfoRef.current = getScanInfo;
  getScanCategoryRef.current = getScanCategory;

  useEffect(() => {
    let isMounted = true;

    const fetchCategoryName = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const scanInfo = await getScanInfoRef.current(scanId, projectId);
        const scanCategoryId = scanInfo?.scan?.scanCategoryId;

        if (!scanCategoryId) {
          if (isMounted) {
            setCategoryName(null);
          }
          return;
        }

        const categoryResponse = await getScanCategoryRef.current(scanCategoryId);
        if (isMounted) {
          setCategoryName(categoryResponse.name || null);
        }
      } catch (err) {
        console.error('Error fetching scan category:', err);
        if (isMounted) {
          setError('Failed to load scan category');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCategoryName();

    return () => {
      isMounted = false;
    };
  }, [scanId, projectId]);

  if (isLoading) {
    return (
      <Typography variant="body2" component="div">
        <Skeleton width={120} />
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  if (!categoryName) {
    return (
      <Typography variant="body2" color="text.secondary">
        No category assigned
      </Typography>
    );
  }

  return <Typography variant="body2">{categoryName}</Typography>;
};
